import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import moment from 'moment'
import { sortBy } from 'lodash'
import { startOfDay, addDays, endOfDay } from 'date-fns'
import * as lz from 'lz-string'
import SVG from 'react-inlinesvg'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  availableGroupBy,
  colors,
  defaultGroupBy,
  allTableColumns,
  availableTableColumns,
  defaultTableColumns,
  filterLabels,
  filterOptions,
  groupLabels,
  tableColumnsKey,
  tableLabels,
} from './constants'
import { StatisticsContextProvider, useStatistics } from './context'
import { Placeholder } from './Placeholder'
import {
  IInsightsV1Statistics,
  INetworksV1List,
  IProfileV1Users,
  Header,
  DatePicker,
  NavFilter,
  TableInfo,
  TableColumns,
  useDatePickerState,
  Search,
  Loadable,
  useApiGet,
  formatFilterRecords,
  useSearch,
  filterTableColumns,
  parseQuery,
  varClass,
  isBlank,
  map,
  plural,
  downloadCsv,
  useOutsideClick,
  capitalize,
  useCounter,
  usePresetPickerState,
  PresetPicker,
} from '../../shared'
import { getCurrentCurrency, getRequest, postRequest } from '../../../services'
import {
  formatFilterDates,
  normalizeFilter,
  normalizeGroupBy,
  statisticsCustomFilter,
  statisticsSort,
  filterCsvColumns,
  newCsvRowFormatter,
  groupStatisticsRows,
  formatCsvFilename,
} from './helpers'
import { Graph, GraphSwitch } from './Graph'
import { Table } from './Table'

export const InsightStatistics = (props) => {
  const { origin } = props

  const datePickerState = useDatePickerState()
  const presetPickerState = usePresetPickerState()
  const currencyCode = getCurrentCurrency()

  const initialLoad = useRef({ loaded: false })
  const initialQuery = useMemo(() => {
    const query = parseQuery(location.search)
    return query
  }, [])
  const initialGroup = useMemo(() => {
    if (isBlank(initialQuery)) {
      return defaultGroupBy
    } else if (
      initialQuery.tab &&
      availableGroupBy.includes(initialQuery.tab)
    ) {
      return initialQuery.tab
    } else {
      return defaultGroupBy
    }
  }, [])
  const initialFilters = useMemo(() => {
    const filters = []
    if (presetPickerState.preset) {
      filters.push(...presetPickerState.filters)
    }
    if (isBlank(initialQuery)) {
      return filters
    } else if (initialQuery.filters) {
      try {
        const initialFilters = lz.decompressFromEncodedURIComponent(
          initialQuery.filters
        )
        if (initialFilters === null) throw 'Incorrect lz-string'
        return JSON.parse(initialFilters)
      } catch (e) {
        try {
          // Backward compatibility
          return JSON.parse(window.decodeURIComponent(initialQuery.filters))
        } catch (e) {
          return filters
        }
      }
    } else {
      return filters
    }
  }, [])
  const lastRequest = useRef({ id: '' })

  useEffect(() => {
    // Special case for "Decreasing performers"
    if (initialQuery?.ecrFilter) {
      datePickerState.setRange({
        ...datePickerState.range,
        startDate: startOfDay(addDays(new Date(), -4)),
        endDate: endOfDay(addDays(new Date(), -1)),
      })
      datePickerState.setPrevRange({
        ...datePickerState.range,
        startDate: startOfDay(addDays(new Date(), -30)),
        endDate: endOfDay(addDays(new Date(), -5)),
      })
      datePickerState.setPrevRangeEnabled(true)
    }
  }, [initialQuery])

  const [prevData, setPrevData] = useState<IInsightsV1Statistics>(null)
  const [data, setData] = useState<IInsightsV1Statistics>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [favorites, setFavorites] = useState(null)
  const [favoritesRef, refreshFavorites] = useCounter()
  const [assignees, setAssignees] = useState(null)
  const [assigneesRef, refreshAssignees] = useCounter()
  const [statuses, setStatuses] = useState(null)
  const [statusesRef, refreshStatuses] = useCounter()
  const [adsStats, setAdsStats] = useState(null)
  const [adsStatsRef, refreshAdsStats] = useCounter()
  const [group, setGroup] = useState(initialGroup)
  const [filters, setFilters] = useState(initialFilters)
  const [selectedGroupByTime, setSelectedGroupByTime] = useState('')
  const [activeSince, setActiveSince] = useState(false)

  // Columns are kept per tab and switch in the same render as the tab, so the
  // statistics request below never goes out with the previous tab's columns
  const columnsKey = tableColumnsKey(group)
  const storedColumns = useMemo(
    () =>
      filterTableColumns(
        localStorage.getItem(columnsKey),
        allTableColumns,
        defaultTableColumns
      ),
    [columnsKey]
  )
  const [columnsByKey, setColumnsByKey] = useState<Record<string, string[]>>({})
  const columns = columnsByKey[columnsKey] ?? storedColumns
  const setColumns = useCallback(
    (value) =>
      setColumnsByKey((prev) => {
        const current = prev[columnsKey] ?? storedColumns
        return {
          ...prev,
          [columnsKey]: typeof value === 'function' ? value(current) : value,
        }
      }),
    [columnsKey, storedColumns]
  )

  const availableColumns = useMemo(() => {
    if (availableTableColumns[group]) {
      return availableTableColumns[group]
    } else {
      return availableTableColumns.default
    }
  }, [group])

  // The per-row network and tracker lists cost most of the query time on URL
  // groupings, so only ask for them while their column is shown
  const withNetworks =
    availableColumns.includes('networks') && columns.includes('networks')
  const withTrackers =
    availableColumns.includes('trackers') && columns.includes('trackers')
  const [trafficFilter, setTrafficFilter] = useState(false)
  const [clickFilter, setClickFilter] = useState(false)

  const [search, setSearch] = useSearch()

  const advertisers = useApiGet('/advs/v1/minlist')
  const channels = useApiGet('/channels/v1/list')
  const networks = useApiGet<INetworksV1List[]>('/networks/v1/list')
  const urlShorts = useApiGet<any>('/tools/v1/urlshorts/list')
  const users = useApiGet<IProfileV1Users[]>('/profile/v1/users')

  useEffect(() => {
    if (group === 'date' || group === 'month' || group === 'year') {
      setGroup(selectedGroupByTime.toLowerCase())
    }
  }, [selectedGroupByTime])

  useEffect(() => {
    const queryGroup = `tab=${group}`
    const queryFilters = filters.length
      ? `&filters=${lz.compressToEncodedURIComponent(JSON.stringify(filters))}`
      : ''
    const newUrl = window.location.pathname + `?${queryGroup}${queryFilters}`
    history.replaceState({ path: newUrl }, '', newUrl)
  }, [group, filters])

  const filterReset = useMemo(() => {
    return sortBy(filters, (filter) => filter.key)
      .filter((filter) => filter.value || filter.key === 'advertiserStatus')
      .map((filter) => filter.value)
      .join(':')
  }, [filters])

  useEffect(() => {
    // Abort the previous request when the filters change or the page closes, so a superseded (and
    // possibly very slow) query is not waited on or retried
    const controller = new AbortController()

    const fetchStatistics = async () => {
      setPrevData(null)
      setData(null)

      const requestId = Date.now().toString()
      lastRequest.current.id = requestId

      const groupBy = normalizeGroupBy(group, datePickerState)

      const enableCompare = datePickerState.prevRangeEnabled
      if (enableCompare) {
        const [result, prevResult] = await Promise.all([
          postRequest(
            '/insights/v1/statistics',
            {
              requestId,
              groupBy,
              ...normalizeFilter({
                datePickerState,
                filters,
                group,
                origin,
                trafficFilter,
                clickFilter,
                activeSince,
                withNetworks,
                withTrackers,
              }),
            },
            controller.signal
          ),
          postRequest(
            '/insights/v1/statistics',
            {
              groupBy,
              ...normalizeFilter({
                datePickerState,
                filters,
                group,
                origin,
                trafficFilter,
                clickFilter,
                withNetworks,
                withTrackers,
                usePrevDates: true,
              }),
            },
            controller.signal
          ),
        ])

        if (result?.data?.requestId !== lastRequest.current.id) {
          return
        }

        setPrevData(prevResult?.data)
        setData(result?.data)
      } else {
        const result = await postRequest(
          '/insights/v1/statistics',
          {
            requestId,
            groupBy,
            ...normalizeFilter({
              datePickerState,
              filters,
              group,
              origin,
              trafficFilter,
              clickFilter,
              activeSince,
              withNetworks,
              withTrackers,
            }),
          },
          controller.signal
        )

        if (result?.data?.requestId !== lastRequest.current.id) {
          return
        }

        setData(result?.data)
      }
    }

    fetchStatistics()

    return () => controller.abort()
  }, [
    group,
    datePickerState,
    filterReset,
    trafficFilter,
    clickFilter,
    activeSince,
    withNetworks,
    withTrackers,
  ])

  useEffect(() => {
    const fetchFavorites = async () => {
      const favorites = await getRequest('/insights/v1/favoritestatistics')
      setFavorites(favorites?.data || [])
    }

    fetchFavorites()
  }, [favoritesRef])

  useEffect(() => {
    const fetchAssignees = async () => {
      const assignees = await getRequest('/advs/v1/assignees')
      setAssignees(assignees?.data || [])
    }

    fetchAssignees()
  }, [assigneesRef])

  useEffect(() => {
    const fetchStatuses = async () => {
      const statuses = await getRequest('/advs/v1/status')
      setStatuses(statuses?.data || [])
    }

    fetchStatuses()
  }, [statusesRef])

  useEffect(() => {
    const fetchAdsStats = async () => {
      const stats = await postRequest('/google/ads/v1/campaigns/stats', {
        fromDate: moment
          .utc(datePickerState.range.startDate)
          .add(1, 'day')
          .startOf('day'),
        toDate: moment.utc(datePickerState.range.endDate).endOf('day'),
      })
      setAdsStats(stats?.data || [])
    }
    fetchAdsStats()
  }, [adsStatsRef])

  useEffect(() => {
    if (data?.rows?.length) {
      if (group === 'shortcode') {
        // Remove Unknown
        data.rows.splice(
          data.rows.findIndex((row) => row.grp === ''),
          1
        )
        // Replace shortcode with Smart link name
        if (urlShorts?.length) {
          for (const row of data.rows) {
            const urlShort = urlShorts.find(
              (urlShort) => urlShort.urlShortCode === row.grp
            )
            if (urlShort) {
              row.grp = urlShort.urlShortName
            }
          }
        }
      }
    }
  }, [data, urlShorts])

  const allAffiliateRef = useRef({ selected: false })
  useEffect(() => {
    if (!networks) return
    const networkFilter = filters.find(
      (filter) => filter.key === 'networkUuids'
    )
    if (!networkFilter) return
    const filterValues = networkFilter.value.split(', ')
    const allAffiliateUuids = networks
      .filter((network) => network.networkName !== 'Heylink Inbound')
      .map((network) => network.networkUuid)

    if (
      filterValues.includes('all') &&
      !allAffiliateUuids.every((uuid) => filterValues.includes(uuid)) &&
      !allAffiliateRef.current.selected
    ) {
      networkFilter.value = ['all', ...allAffiliateUuids].join(', ')
      setFilters([...filters])
      allAffiliateRef.current.selected = true
    } else if (
      !filterValues.includes('all') &&
      allAffiliateRef.current.selected
    ) {
      networkFilter.value = ''
      setFilters([...filters])
      allAffiliateRef.current.selected = false
    }

    if (filterValues.includes('all')) {
      networkFilter.label = 'All affiliate networks'
    }
  }, [filters])

  useEffect(() => {
    if (columns) {
      setActiveSince(columns.includes('activeSinceDatetime'))
    }
  }, [columns])

  useEffect(() => {
    if (initialLoad.current.loaded) {
      setSearch('')
    } else {
      initialLoad.current.loaded = true
    }
  }, [group])

  useEffect(() => {
    if (group === 'boost') {
      const conversionBoostAddedValue = 'conversionBoostAddedValue'
      if (columns && !columns.includes(conversionBoostAddedValue)) {
        // Insert added value column
        setColumns((prev) => {
          const newColumns: any = [...prev]
          const lastCommissionIndex = newColumns.findLastIndex((column) =>
            column?.endsWith('Commission')
          )
          if (lastCommissionIndex === -1) {
            newColumns.push(conversionBoostAddedValue)
          } else {
            newColumns.splice(
              lastCommissionIndex + 1,
              0,
              conversionBoostAddedValue
            )
          }
          return newColumns
        })
      }
    }
  }, [group, columns])

  const [crFilter, setCrFilter] = useState(initialQuery?.crFilter)
  const [ecrFilter, setEcrFilter] = useState(initialQuery?.ecrFilter)
  const [favFilter, setFavFilter] = useState(false)
  const [showGraph, setShowGraph] = useState(false)
  const [graphHeight, setGraphHeight] = useState(0)
  const [graphColumns, setGraphColumns] = useState([
    'commission',
    'conversions',
    'clicks',
    'pageviews',
  ])

  const contextValue = {
    filters,
    setFilters,
    datePickerState,
    advertisers,
    channels,
    networks,
    users,
    search,
    setSearch,
    data,
    prevData,
    totalCount,
    setTotalCount,
    favorites,
    setFavorites,
    refreshFavorites,
    assignees,
    setAssignees,
    refreshAssignees,
    statuses,
    setStatuses,
    refreshStatuses,
    adsStats,
    refreshAdsStats,
    group,
    setGroup,
    columns,
    setColumns,
    availableColumns,
    crFilter,
    setCrFilter,
    ecrFilter,
    setEcrFilter,
    favFilter,
    setFavFilter,
    clickFilter,
    setClickFilter,
    trafficFilter,
    setTrafficFilter,
    showGraph,
    setShowGraph,
    graphHeight,
    setGraphHeight,
    graphColumns,
    setGraphColumns,
    currencyCode,
    setSelectedGroupByTime,
    selectedGroupByTime,
  }

  return (
    <>
      <Header
        label={origin ? `${capitalize(origin)} Statistics` : 'All Statistics'}
      />
      <StatisticsContextProvider value={contextValue}>
        <Nav />
        <div
          className={varClass({
            'card insights-statistics-card': true,
            'show-graph': showGraph,
          })}
          style={
            {
              '--graph-height': `${graphHeight}px`,
            } as any
          }
        >
          <GroupSelect />

          <div className="card-divider" />
          <div className="card-body card-body-fill">
            <StatNav />
            <Loadable
              loaded={data && favorites && assignees && statuses}
              placeholder={<Placeholder />}
            >
              <DndProvider backend={HTML5Backend}>
                <Stats />
              </DndProvider>
            </Loadable>
          </div>
        </div>
        <PresetPicker className="in-header" />
        <DatePicker className="in-header" enableCompare />
      </StatisticsContextProvider>
    </>
  )
}

const Nav = (props) => {
  const {
    filters,
    setFilters,
    datePickerState,
    advertisers,
    channels,
    networks,
    users,
  } = useStatistics()

  const dateOptions = useMemo(
    () => formatFilterDates(datePickerState),
    [datePickerState]
  )

  return (
    <>
      <div className="page-nav m-b-4">
        <NavFilter
          {...{
            filters,
            setFilters,
            filterLabels,
            filterOptions: {
              date: dateOptions,
              ...filterOptions,
              names: formatFilterRecords(
                advertisers,
                'advertiserDomain',
                'advertiserDomain'
              ),
              channelUuids: formatFilterRecords(
                channels,
                'channelUuid',
                'channelName',
                'channelDomain'
              ),
              networkUuids: {
                'all': 'All affiliate networks',
                ...formatFilterRecords(networks, 'networkUuid', 'networkName'),
              },
              assigneeUserUuids: {
                ...formatFilterRecords(
                  users,
                  'userUuid',
                  (user) => `${user.userFirstname} ${user.userLastname}`
                ),
                none: 'No manager',
              },
              boostDepartment: {
                'creator': 'Creator',
                'sales': 'Sales',
                'affiliate': 'Affiliate',
              },
              'subIds': 'input',
              'subId1s': 'input',
              'subId2s': 'input',
              'subId3s': 'input',
              'subId4s': 'input',
              'subId5s': 'input',
              'tags': 'pair',
            },
          }}
          isMultipleChoice
          noMultipleChoice={[
            'date',
            'subIds',
            'subId1s',
            'subId2s',
            'subId3s',
            'subId4s',
            'subId5s',
            'tags',
          ]}
        />
      </div>
    </>
  )
}

const GroupSelect = (props) => {
  const { group, setGroup, filters } = useStatistics()

  const groupItemProps = { group, setGroup, filters }

  return (
    <div className="page-subnav">
      <div className="nav">
        <ul>
          <GroupItem title="Merchant" value="name" {...groupItemProps} />
          <GroupItem title="Campaign" value="tracker" {...groupItemProps} />
          <GroupItem title="Site" value="channel" {...groupItemProps} />
          <GroupItem title="Network" value="network" {...groupItemProps} />
          <GroupItem title="Smart link" value="shortcode" {...groupItemProps} />
          <li className="divider" />
          <GroupItem
            title="Date"
            value="date"
            values={['date', 'month', 'year']}
            {...groupItemProps}
          />
          <GroupItem title="Target" value="target" {...groupItemProps} />
          <GroupItem
            title="Network account"
            value="connection"
            {...groupItemProps}
          />
          <GroupItem title="Page" value="page" {...groupItemProps} />
          <GroupItem title="Device" value="device" {...groupItemProps} />
          <GroupItem title="Currency" value="currency" {...groupItemProps} />
          <GroupItem title="Boost" value="boost" {...groupItemProps} />
          {/*
            <GroupItem title="Referrer" value="referer" {...groupItemProps} />
            {isRevshareSource && (
              <GroupItem title="Boost" value="revshare" {...groupItemProps} />
            )}
            */}
        </ul>
      </div>
    </div>
  )
}

const GroupItem = (props) => {
  const { title, value, values, group, setGroup } = props

  return (
    <li
      className={
        group === value || values?.some((v) => v === group) ? 'active' : ''
      }
    >
      <button
        className="link"
        onClick={() => {
          setGroup(value)
        }}
      >
        {title}
      </button>
    </li>
  )
}

const Stats = (props) => {
  const { data, prevData, group, datePickerState } = useStatistics()

  const [sort, setSort] = useState('conversionTotalCommission')
  const [dir, setDir] = useState('desc')
  const [prevSort, setPrevSort] = useState('')
  const [prevDir, setPrevDir] = useState('desc')
  const [unknownRow, setUnknownRow] = useState(null)
  const toggleSort = (newSort: string, defaultDir: 'asc' | 'desc') => () => {
    if (sort === newSort) {
      if (dir === 'asc') {
        setDir('desc')
      } else {
        setDir('asc')
      }
    } else {
      setPrevSort(sort)
      setPrevDir(dir)
      setSort(newSort)
      setDir(defaultDir)
    }
  }

  if (!data.totals) {
    return null
  }

  useEffect(() => {
    const newUnknownRow = data?.rows?.find(
      (item) =>
        (!item.name && !item.grp) ||
        (item.name && item.name.toLowerCase() === 'unknown')
    )
    const newPrevUnknownRow = prevData?.rows?.find(
      (item) =>
        (!item.name && !item.grp) ||
        (item.name && item.name.toLowerCase() === 'unknown')
    )
    if (newUnknownRow && newPrevUnknownRow) {
      newUnknownRow.prev = newPrevUnknownRow
    }
    setUnknownRow(newUnknownRow)
  }, [data])

  // const isRevshareSource = useMemo(
  //   () => getCurrentUser()?.user?.userAccountRevshareSource,
  //   []
  // )

  const isSingleDate =
    group === 'date' &&
    moment(datePickerState.range.startDate).format('YYYY-MM-DD') ===
      moment(datePickerState.range.endDate).format('YYYY-MM-DD')

  return (
    <>
      <Graph
        {...{
          isSingleDate,
        }}
      />
      <Table
        {...{
          sort,
          dir,
          prevSort,
          prevDir,
          toggleSort,
          isSingleDate,
          unknownRow,
        }}
      />
    </>
  )
}

const StatNav = (props) => {
  const { sort, dir, prevSort, prevDir } = props
  const {
    data,
    prevData,
    totalCount,
    group,
    search,
    setSearch,
    columns,
    setColumns,
    availableColumns,
    filters,
    favorites,
    networks,
    assignees,
    statuses,
    adsStats,
    crFilter,
    setCrFilter,
    ecrFilter,
    setEcrFilter,
    favFilter,
    setFavFilter,
    clickFilter,
    setClickFilter,
    trafficFilter,
    setTrafficFilter,
    showGraph,
    setShowGraph,
    graphColumns,
    setGraphColumns,
    datePickerState,
    currencyCode,
    setSelectedGroupByTime,
    selectedGroupByTime,
  } = useStatistics()

  return (
    <TableInfo>
      {showGraph ? (
        <>
          <div className="graph-columns">
            {group === 'date' && (
              <GraphSwitch
                title="Pageviews"
                value="pageviews"
                selectedValues={graphColumns}
                setValues={setGraphColumns}
                color={colors.pageviews}
              />
            )}
            <GraphSwitch
              title="Clicks"
              value="clicks"
              selectedValues={graphColumns}
              setValues={setGraphColumns}
              color={colors.clicks}
            />
            <GraphSwitch
              title="Conversions"
              value="conversions"
              selectedValues={graphColumns}
              setValues={setGraphColumns}
              color={colors.conversions}
            />
            <GraphSwitch
              title="Revenue"
              value="commission"
              selectedValues={graphColumns}
              setValues={setGraphColumns}
              color={colors.commission}
            />
          </div>
        </>
      ) : (
        <Search
          value={search}
          setValue={setSearch}
          placeholder={`Find ${groupLabels[group]?.toLowerCase()}`}
          className="search flex-1"
        />
      )}
      <div className="vertical-middle">
        <TableColumns
          storageKey={tableColumnsKey(group)}
          columns={columns}
          setColumns={setColumns}
          availableColumns={availableColumns}
          columnLabels={tableLabels}
        />
        <button
          className="link action m-l-15"
          onClick={() => {
            const dataRows = groupStatisticsRows({
              data,
              prevData,
              group,
              datePickerState,
            })
            const rows: any = statisticsSort(
              statisticsCustomFilter({
                data: dataRows,
                prevData: prevData?.rows,
                search,
                filters,
                favorites,
                assignees,
                statuses,
                group,
                crFilter,
                ecrFilter,
                favFilter,
              }),
              [
                { sort: prevSort, dir: prevDir },
                { sort, dir },
              ]
            )
            const csvColumns = filterCsvColumns(columns, availableColumns)
            downloadCsv<any>({
              filename: formatCsvFilename({ group, datePickerState }),
              columns: csvColumns,
              rows,
              columnFormatter: (column) =>
                tableLabels[column] || groupLabels[group],
              rowFormatter: newCsvRowFormatter({
                group,
                columns: csvColumns,
                statuses,
                networks,
                assignees,
                adsStats,
                currencyCode,
              }),
            })
          }}
        >
          <SVG src="/images/insights/icon-csv.svg" className="m-r-10" />
          CSV
        </button>
        <button
          className={varClass({
            'link action m-l-15': true,
            'active': crFilter,
          })}
          onClick={() => setCrFilter(!crFilter)}
        >
          <SVG src="/images/aside-conversions.svg" className="m-r-10" />
          &lt;0,8% CR
        </button>
        <button
          className={varClass({
            'link action m-l-15': true,
            'active': clickFilter,
          })}
          onClick={() => setClickFilter(!clickFilter)}
        >
          <SVG src="/images/insights/icon-cursor.svg" className="m-r-1" />
          50+ clicks
        </button>
        {datePickerState.prevRangeEnabled && (
          <button
            className={varClass({
              'link action m-l-15': true,
              'active': ecrFilter,
            })}
            onClick={() => setEcrFilter(!ecrFilter)}
          >
            <SVG src="/images/aside-conversions.svg" className="m-r-10" />
            -15% ECR
          </button>
        )}
        <button
          className={varClass({
            'link action m-l-15': true,
            'active': showGraph,
          })}
          onClick={() => setShowGraph(!showGraph)}
        >
          <SVG src="/images/insights/icon-graph.svg" className="m-r-10" />
          Graph
        </button>
        {group === 'name' && (
          <button
            className={varClass({
              'link action m-l-15': true,
              'active': favFilter,
              'hidden': isBlank(favorites),
            })}
            onClick={() => setFavFilter(!favFilter)}
          >
            {favFilter ? (
              <SVG
                src="/images/icon-star-filled.svg"
                className="m-r-10 text-new-orange"
              />
            ) : (
              <SVG src="/images/icon-star.svg" className="m-r-10" />
            )}
            Favorites
          </button>
        )}
        <button
          className={varClass({
            'link action m-l-15': true,
            'active': trafficFilter,
          })}
          onClick={() => setTrafficFilter(!trafficFilter)}
        >
          <SVG src="/images/insights/icon-swap.svg" className="m-r-1" />
          Split tests
        </button>
        {(group === 'date' || group === 'month' || group === 'year') && (
          <GroupByTimeSelect
            className={'m-l-15'}
            group={group}
            setSelected={setSelectedGroupByTime}
            selected={selectedGroupByTime}
          />
        )}
        <div className="total">
          {totalCount} {plural(totalCount, 'result')}
        </div>
      </div>
    </TableInfo>
  )
}

const GroupByTimeSelect = (props) => {
  const { className, selected, setSelected, group } = props
  const criteria = useMemo(() => ['Date', 'Month', 'Year'], [])

  const [open, setOpen] = useState(false)

  const ref = useRef<HTMLDivElement>()
  useOutsideClick(ref, () => {
    setOpen(false)
  })

  return (
    <div className="group-by-time" ref={ref}>
      <button
        className={varClass({
          'time-select link action': true,
          [className]: !!className,
        })}
        onClick={() => {
          setOpen(!open)
        }}
      >
        <SVG src="/images/insights/icon-calendar.svg" className="m-r-10" />
        Group by
        <SVG src="/images/chevron-down.svg" className="m-l-4 m-r--15" />
      </button>

      <div
        className={varClass({
          'table-columns-menu': true,
          'shown': open,
        })}
      >
        <ul>
          {map(criteria, (c, index) => {
            return (
              <li
                key={c}
                className={varClass({
                  'selected': group === c.toLowerCase(),
                })}
                onClick={() => {
                  setSelected(c)
                }}
              >
                {c}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
